import { create } from 'zustand';
import { authApi } from '@/lib/api';
import { connectSocket, disconnectSocket } from '@/lib/socket';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  hasTelegram: boolean;
  emailVerified: boolean;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  sessionExpired: boolean;

  login: (accessToken: string, refreshToken: string, user: AuthUser) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  isAuthenticated: () => boolean;
  clearSessionExpired: () => void;
}

// The API accepts each refresh token once, so two concurrent refreshes with
// the same token race: the loser gets a 401 and would log the user out even
// though the other succeeded. Concurrent callers share one refresh instead —
// e.g. React StrictMode mounting App twice in development, or App's startup
// refresh overlapping a request's 401 retry (lib/api.ts).
let inFlightRefresh: Promise<boolean> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: sessionStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  isLoading: false,
  sessionExpired: false,

  login: (accessToken, refreshToken, user) => {
    sessionStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    set({ user, accessToken, refreshToken, sessionExpired: false });
    connectSocket({ getToken: () => get().accessToken, refresh: () => get().refreshSession() });
  },

  logout: async () => {
    const { refreshToken } = get();
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => {});
    }
    sessionStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    set({ user: null, accessToken: null, refreshToken: null });
    disconnectSocket();
  },

  refreshSession: () => {
    inFlightRefresh ??= (async () => {
      const { refreshToken } = get();
      if (!refreshToken) {
        await get().logout();
        set({ sessionExpired: true });
        return false;
      }

      try {
        const result = await authApi.refresh(refreshToken);
        get().login(result.accessToken, result.refreshToken, result.user);
        return true;
      } catch {
        await get().logout();
        set({ sessionExpired: true });
        return false;
      }
    })().finally(() => {
      inFlightRefresh = null;
    });
    return inFlightRefresh;
  },

  clearSessionExpired: () => set({ sessionExpired: false }),

  isAuthenticated: () => !!get().accessToken && !!get().user,
}));
